import Spine, { Collection } from '@finviz/spine'
import omit from 'lodash.omit'

import { IIdea, RootChartConfigObject, Theme, TodoObjectHashAnyType } from '../../types/shared'
import { ChartEditorEnum, IndicatorType, LayoutType, SpecificChartFunctionality } from '../constants/common'
import Indicator from '../indicators/indicator'
import Chart from './chart'
import { ChartSyncablePart } from './chart/contstants'
import { ISettings } from './chart_settings/interfaces'
import { AutoSaveState } from './constants'
import Pane from './pane'

class ChartLayout extends Spine.Model {
  static initClass(chartModel: typeof Chart) {
    this.configure(
      'ChartLayout',
      'layout',
      'activeChart',
      'activePane',
      'width',
      'height',
      'editable',
      'canChangeTicker',
      'scrollable',
      'initialScrollable',
      'cross',
      'editors',
      'theme',
      'settings',
      'isWheelZoomEnabled',
      'specificChartFunctionality',
      'isLargeChart',
      'isHideDrawingsActive',
      'isPreserveDrawingsActive',
      'isLockDrawingsActive',
      'isDrawingModeContinuousActive',
      'uuid',
      'isTouchCrossActive',
      'defaultZoomFactor',
      'syncChartParts',
      'isInit',
      'idea',
      'autoSaveState'
    )
    this.hasMany('charts', chartModel)
  }

  declare layout: LayoutType
  declare activeChart: Chart
  declare activePane?: Pane
  declare width: number
  declare height: number
  declare editable: boolean
  declare canChangeTicker: boolean
  declare scrollable: boolean
  declare readonly initialScrollable: boolean
  declare cross: boolean
  declare editors: ChartEditorEnum[]
  declare theme: Theme
  declare settings: ISettings
  declare isWheelZoomEnabled: boolean
  declare specificChartFunctionality: SpecificChartFunctionality
  declare isLargeChart?: boolean
  declare charts: () => Collection<Chart>
  declare isHideDrawingsActive: boolean
  declare isPreserveDrawingsActive: boolean
  declare isLockDrawingsActive: boolean
  declare isDrawingModeContinuousActive: boolean
  declare uuid: string
  declare isTouchCrossActive?: boolean
  declare defaultZoomFactor: number
  declare syncChartParts: ChartSyncablePart[]
  declare idea?: IIdea
  declare isInit?: boolean
  autoSaveState: AutoSaveState[] = [AutoSaveState.Off]

  getPaneWithSelection() {
    for (const pane of this.getAllPanes()) {
      if (pane.selection) {
        return pane
      }
    }
  }

  toObject() {
    const charts = this.getAllCharts().map((chart) => chart.toObject())
    return {
      layout: this.layout,
      width: this.width,
      height: this.height,
      charts,
      isHideDrawingsActive: this.isHideDrawingsActive,
      isPreserveDrawingsActive: this.isPreserveDrawingsActive,
    }
  }

  toConfig<T extends Partial<RootChartConfigObject> = RootChartConfigObject>(omitKeys = [] as string[]): T {
    const charts = this.getAllCharts().map((chart) => chart.toConfig(omitKeys))
    return JSON.parse(
      JSON.stringify(
        omit(
          {
            ...this.settings.ChartSettings.center,
            layout: this.layout,
            width: this.width,
            height: this.height,
            charts,
            isHideDrawingsActive: this.isHideDrawingsActive,
            isPreserveDrawingsActive: this.isPreserveDrawingsActive,
            colors: this.settings.ChartSettings.general.Colors,
            specificChartFunctionality: this.specificChartFunctionality,
            editors: this.editors,
            scrollable: this.scrollable,
            theme: this.theme,
            newCharts: /\bcharts\b/.test(window.location.pathname),
          },
          omitKeys
        )
      )
    )
  }

  destroyCascade(options?: TodoObjectHashAnyType) {
    this.getAllCharts().forEach((chart) => {
      chart.destroyCascade()
    })
    return this.destroy(options)
  }

  getAllCharts() {
    return this.charts().all()
  }

  getAllPanes() {
    return this.getAllCharts().flatMap((chart) => chart.getAllPanes())
  }

  getAllElements() {
    return this.getAllCharts().flatMap((chart) => chart.getAllElements())
  }

  setSyncChartParts(chartParts: ChartSyncablePart | ChartSyncablePart[], isInSync: boolean) {
    const chartPartsToSync = Array.isArray(chartParts) ? chartParts : [chartParts]

    const newSyncedChartParts = this.syncChartParts?.filter((chartPart) => !chartPartsToSync.includes(chartPart)) ?? []

    if (isInSync) {
      newSyncedChartParts.push(...chartPartsToSync)
    }

    this.updateAttributes({ syncChartParts: newSyncedChartParts })
  }

  getIsChartPartInSync(chartPart: ChartSyncablePart) {
    return !!this.syncChartParts?.includes(chartPart)
  }

  getIsIndicatorPresent(type: IndicatorType) {
    return !!this.charts()
      .first()
      ?.getAllElements()
      .some((element) => element.isIndicator() && (element.instance as unknown as Indicator).type === type)
  }

  getHTMLElementId() {
    return `chart-layout-${this.id}`
  }

  isIdeaId(id: number | null = null) {
    return (this.idea?.id ?? null) === id
  }

  setAutoSaveState = (newState: AutoSaveState) => {
    switch (newState) {
      case AutoSaveState.Off: {
        this.updateAttribute('autoSaveState', [AutoSaveState.Off])
        break
      }
      case AutoSaveState.Saved: {
        if (
          !this.autoSaveState.some((item) =>
            [
              AutoSaveState.UnsavedDrawings,
              AutoSaveState.UnsavedNote,
              AutoSaveState.SavingDrawings,
              AutoSaveState.SavingNote,
            ].includes(item)
          )
        ) {
          this.updateAttribute('autoSaveState', [AutoSaveState.Saved])
        }
        break
      }
      case AutoSaveState.SavedDrawings: {
        if (this.autoSaveState.some((item) => [AutoSaveState.UnsavedNote, AutoSaveState.SavingNote].includes(item))) {
          this.updateAttribute(
            'autoSaveState',
            this.autoSaveState.filter((item) => item !== AutoSaveState.SavingDrawings)
          )
        } else {
          this.updateAttribute('autoSaveState', [AutoSaveState.Saved])
        }
        break
      }
      case AutoSaveState.SavedNote: {
        if (
          this.autoSaveState.some((item) =>
            [AutoSaveState.UnsavedDrawings, AutoSaveState.SavingDrawings].includes(item)
          )
        ) {
          this.updateAttribute(
            'autoSaveState',
            this.autoSaveState.filter((item) => item !== AutoSaveState.SavingNote)
          )
        } else {
          this.updateAttribute('autoSaveState', [AutoSaveState.Saved])
        }
        break
      }
      case AutoSaveState.UnsavedDrawings: {
        this.updateAttribute('autoSaveState', [
          ...this.autoSaveState.filter((item) => item !== AutoSaveState.Saved),
          AutoSaveState.UnsavedDrawings,
        ])
        break
      }
      case AutoSaveState.SavingDrawings: {
        this.updateAttribute('autoSaveState', [
          ...this.autoSaveState.filter((item) => item !== AutoSaveState.UnsavedDrawings),
          AutoSaveState.SavingDrawings,
        ])
        break
      }
      case AutoSaveState.UnsavedNote: {
        this.updateAttribute('autoSaveState', [
          ...this.autoSaveState.filter((item) => item !== AutoSaveState.Saved),
          AutoSaveState.UnsavedNote,
        ])
        break
      }
      case AutoSaveState.SavingNote: {
        this.updateAttribute('autoSaveState', [
          ...this.autoSaveState.filter((item) => item !== AutoSaveState.UnsavedNote),
          AutoSaveState.SavingNote,
        ])
        break
      }
      default:
        break
    }
  }

  getIsAutoSaveSavingInProgress() {
    return this.autoSaveState.some((item) => ![AutoSaveState.Off, AutoSaveState.Saved].includes(item))
  }
}

export default ChartLayout
